import { GridApi } from 'ag-grid-community';
import { DATE_RANGE_NUMERIC_VALUES } from 'components/Dashboard/Dashboard.context.helpers';
import {
  LOCAL_STORAGE_KEY_SETTINGS,
  TApplicationTableStateContextValue,
} from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { TApplicationFormalOffer } from 'lendingApi/applications.types';
import _ from 'lodash';
import {
  ALL_STATUS_VALUES,
  APPLICATION_TABLE_KEYS,
  PRODUCT_DISPLAY_ORDER,
} from 'utils/applications/applications.config';
import { deriveProductFromTypeAndDuration } from 'utils/productHelper';

export function isCrossSellProduct(
  formal_offer_product: string,
  requested_product?: string | null,
) {
  if (!requested_product) return false;

  if (
    formal_offer_product === 'credit_facility' &&
    requested_product === 'recovery_loan'
  ) {
    return true;
  }

  return false;
}

/**
 * Reorder the selected fields to match the cred_facility,flexi-24,term_loan arrangement.
 * To ensure offers are displayed in a consistent order that also matches the filters check
 */
function sortProductNames(productNames: string[]): string[] {
  const orderedArr = PRODUCT_DISPLAY_ORDER.filter((product) =>
    productNames.includes(product),
  );
  const remainingArr = productNames.filter(
    (product) => !PRODUCT_DISPLAY_ORDER.includes(product),
  );
  return Array.from(new Set([...orderedArr, ...remainingArr]));
}

export function getProductNames(data: any, isUK: boolean) {
  const formalOffers = data?.formal_offer_sets[0]?.formal_offers;
  const hasMultipleFormalOffers = formalOffers?.length > 1;

  const selectedOffer = formalOffers?.find((offer: TApplicationFormalOffer) => {
    return offer.is_selected;
  });

  const duration = data?.requested_products[0]?.duration; // Product type can be intentionally undefined to show the dash (-) instead

  if (hasMultipleFormalOffers && !selectedOffer) {
    return sortProductNames(
      formalOffers.map(
        ({
          product_type,
          duration: formalOfferDuration,
        }: TApplicationFormalOffer) =>
          deriveProductFromTypeAndDuration({
            productType: product_type,
            duration: formalOfferDuration,
            isUK,
          }).name,
      ),
    ).join(',');
  } else {
    let productType;

    if (selectedOffer) {
      productType = selectedOffer?.product_type;
    } else if (formalOffers?.[0]?.product_type) {
      productType = formalOffers[0].product_type;
    } else {
      productType = data?.requested_products?.[0]?.product_type;
    }

    return deriveProductFromTypeAndDuration({
      productType,
      duration,
      isUK,
    }).name;
  }
}

export const bindListFilters = (
  gridApi: GridApi,
  {
    getFilterValues,
  }: Pick<TApplicationTableStateContextValue, 'getFilterValues'>,
) => {
  for (const tableKey of [
    APPLICATION_TABLE_KEYS.status,
    APPLICATION_TABLE_KEYS.product_type,
    APPLICATION_TABLE_KEYS.term_length,
  ]) {
    const tableKeyFilters = getFilterValues(tableKey);

    // Setting value to 'tableKeyFilters || ALL_STATUS_VALUES' here incase 'All' was selected in the external filters
    if (tableKey === APPLICATION_TABLE_KEYS.status) {
      gridApi.setColumnFilterModel(APPLICATION_TABLE_KEYS.status, {
        value: tableKeyFilters || ALL_STATUS_VALUES,
      });
    } else if (tableKeyFilters) {
      gridApi.setColumnFilterModel(tableKey, { value: tableKeyFilters });
    }
    gridApi.onFilterChanged();
  }
};

export const registerNativeAgGridFilters = (
  gridApi: GridApi,
  {
    saveFilter,
    removeFilter,
    getFilterValues,
  }: Pick<
    TApplicationTableStateContextValue,
    'saveFilter' | 'removeFilter' | 'getFilterValues'
  >,
) => {
  Object.values(APPLICATION_TABLE_KEYS).forEach((key) => {
    if (
      key !== APPLICATION_TABLE_KEYS.status &&
      key !== APPLICATION_TABLE_KEYS.product_type &&
      key !== APPLICATION_TABLE_KEYS.term_length
    ) {
      const currentFilter = gridApi.getColumnFilterModel(key);
      const existingFilter = getFilterValues(key);

      if (!_.isEqual(existingFilter || {}, currentFilter || {})) {
        if (currentFilter) {
          saveFilter(key, currentFilter);
        } else {
          removeFilter(key);
        }
      }
    }
  });
  gridApi.onFilterChanged();
};

export const getDefaultDateRangeNumber = () => {
  const storedSettings = JSON.parse(
    localStorage?.getItem(LOCAL_STORAGE_KEY_SETTINGS) || '{}',
  );
  const defaultDateFromNumber = storedSettings?.dateRange
    ? DATE_RANGE_NUMERIC_VALUES[storedSettings.dateRange]
    : 1;

  return defaultDateFromNumber || null;
};

export const getDefaultIncludePartnerChannel = () => {
  const storedSettings = JSON.parse(
    localStorage?.getItem(LOCAL_STORAGE_KEY_SETTINGS) || '{}',
  );
  const defaultIncludePartnerChannel = Boolean(
    storedSettings?.includePartnerChannel,
  );

  return defaultIncludePartnerChannel;
};
